import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { resetPassword } from "../../api";
import "../sharedAuth.css";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isInviteFlow = searchParams.get("flow") === "invite";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("This reset link is invalid or expired. Request a new link.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetPassword({
        token: token,
        newPassword,
      });
      if (result.ok) {
        setSuccess("Password reset successful. Redirecting to sign in...");
        setTimeout(() => navigate("/signin", { replace: true }), 1200);
      } else {
        setError(result.message || "Unable to reset password");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">
          {isInviteFlow ? "Set your password" : "Reset password"}
        </h1>
        <p className="auth-subtitle">
          {isInviteFlow
            ? "Set your password to activate your account and continue to sign in."
            : "Set your new password to finish resetting your account."}
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error ? (
            <p className="auth-alert auth-alert-error">{error}</p>
          ) : null}
          {success ? (
            <p className="auth-alert auth-alert-success">{success}</p>
          ) : null}

          <label className="auth-label" htmlFor="newPassword">
            New password
          </label>
          <div className="auth-inputWrap auth-passwordWrap">
            <input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              className="auth-input auth-input-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-passwordToggle"
              onClick={() => setShowNewPassword((shown) => !shown)}
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? (
                <EyeOff className="auth-passwordIcon" aria-hidden="true" />
              ) : (
                <Eye className="auth-passwordIcon" aria-hidden="true" />
              )}
            </button>
          </div>

          <label className="auth-label" htmlFor="confirmPassword">
            Confirm password
          </label>
          <div className="auth-inputWrap auth-passwordWrap">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              className="auth-input auth-input-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth-passwordToggle"
              onClick={() => setShowConfirmPassword((shown) => !shown)}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? (
                <EyeOff className="auth-passwordIcon" aria-hidden="true" />
              ) : (
                <Eye className="auth-passwordIcon" aria-hidden="true" />
              )}
            </button>
          </div>

          <button className="auth-button" disabled={isLoading} type="submit">
            {isLoading ? "Saving..." : "Reset password"}
          </button>
        </form>

        <p className="auth-footerText">
          <Link to="/signin" className="auth-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
